/**
 * /pressure-testing/certificates — the certificate register (PT-4,
 * fieldiq-api#921 `GET /pressure-testing/certificates`), full width. A row
 * opens the review on its own page, /pressure-testing/certificates/:certificateId
 * (CertificateDetailScreen); nothing opens beside or over the register, since
 * split panes and drawers are banned (CLAUDE.md). The status chip and the
 * search live in the URL (`?status=`, `?q=`) so the record's back arrow comes
 * back to them; a legacy `?certificate=<id>` link is forwarded to the record.
 *
 * EVERYTHING ON A ROW IS THE API'S: number (or "Draft" — a draft has none),
 * kind, status and `flag_count` are read as returned. The Tests column is the
 * job's own `test_counts` from `GET /jobs`, joined on job_id — the certificate
 * list carries no test tally, and counting the certificate's tests here would
 * be a second answer to a question the job already answers.
 *
 * THE CHIP AND BAND COUNTS ARE TALLIES OF THE UNFILTERED LIST, honest only
 * because the list is unpaginated (the API answers every certificate) and no
 * endpoint returns a certificate count. If it is ever paginated, they must come
 * from the API.
 *
 * Status filtering is the API's `status` param; search is its `q` (number, job,
 * customer, location, UWI, LSD). "Awaiting issue" is status `draft`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertOctagon, CheckCircle2, ChevronRight, FileClock, Loader2, Search, Undo2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { usePtCertificates, usePtJobs } from '../../hooks/usePressureTesting';
import { formatInstantTenantDay } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { ptCertKindSkin, ptCertStatusSkin } from '../../lib/pressure-testing';
import { PT_CERTIFICATE_STATUSES, type PtCertificateListRow, type PtCertificateStatus, type PtJob } from '../../types/pressure-testing';
import { PtChip, PtEmpty, PtPanel, PtSkinBadge } from './PtBits';
import { useOpenRecord } from '../downhole/recordPage';
import { useUrlBackedText } from '../../hooks/useUrlBackedText';
import { testsPassedText } from './TestJobsScreen';

const isCertStatus = (v: string | null): v is PtCertificateStatus => !!v && (PT_CERTIFICATE_STATUSES as readonly string[]).includes(v);

const CHIPS: { key: '' | PtCertificateStatus; label: string }[] = [
  { key: 'draft', label: 'Awaiting issue' },
  { key: 'issued', label: 'Issued' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

/** The day that matters for the row: issued on, else completed on — both in the tenant's zone. */
function rowDate(c: PtCertificateListRow, tz: string | null | undefined): { text: string; sub: string } {
  if (c.status === 'issued') return { text: formatInstantTenantDay(c.issued_at, tz) ?? '—', sub: 'Issued' };
  return { text: formatInstantTenantDay(c.completed_at, tz) ?? '—', sub: 'Job completed' };
}

/** "—" when the API sent no flag count; a real 0 is a 0. */
const flagText = (n: number | null | undefined) => (typeof n === 'number' ? String(n) : '—');

export default function CertificatesScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  // Lands on Awaiting issue: the pile someone has to act on.
  const status: '' | PtCertificateStatus = statusParam === 'all' ? '' : isCertStatus(statusParam) ? statusParam : 'draft';
  const openRecord = useOpenRecord();
  const legacyCertificate = searchParams.get('certificate');
  // The box writes the URL as it is typed; the API is asked once typing pauses.
  const search = searchParams.get('q') ?? '';
  const [q, setQ] = useState(() => search.trim());

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(() => ({ ...(status && { status }), ...(q && { q }) }), [status, q]);
  const listQuery = usePtCertificates(params);
  // The unfiltered list, for the counts. Same key as the All chip, so one request serves both.
  const allQuery = usePtCertificates();
  const jobsQuery = usePtJobs();

  const meta = allQuery.data?.meta ?? listQuery.data?.meta;
  const tz = meta?.time_zone ?? null;
  const listFailure = queryLoadFailure(listQuery);
  const rows = listQuery.data?.data ?? [];
  const allFailed = !!queryLoadFailure(allQuery);
  const all = allQuery.isLoading || allFailed ? undefined : allQuery.data?.data;
  const countsLoading = allQuery.isLoading;
  const jobsById = useMemo(() => new Map((jobsQuery.data?.data ?? []).map((j) => [j.id, j] as const)), [jobsQuery.data]);

  const tally = (pick: (c: PtCertificateListRow) => boolean) => (all ? all.filter(pick).length : undefined);
  const chipCount = (key: '' | PtCertificateStatus) =>
    countsLoading ? undefined : displayValue(key ? tally((c) => c.status === key) : tally(() => true));

  const setStatus = (next: '' | PtCertificateStatus) => {
    const p = new URLSearchParams(searchParams);
    p.set('status', next || 'all');
    setSearchParams(p, { replace: true });
  };

  const setSearch = (next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set('q', next);
    else p.delete('q');
    setSearchParams(p, { replace: true });
  };
  /* The box keeps its own text and writes it through; see useUrlBackedText. */
  const [searchInput, onSearchInput] = useUrlBackedText(search, setSearch);

  const open = (id: string) => openRecord(`/pressure-testing/certificates/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  const testsFor = (c: PtCertificateListRow) => {
    if (jobsQuery.isLoading || queryLoadFailure(jobsQuery)) return '—';
    const job: PtJob | undefined = jobsById.get(c.job_id);
    return job ? testsPassedText(job) : '—';
  };

  if (legacyCertificate) return <Navigate to={`/pressure-testing/certificates/${legacyCertificate}`} replace />;

  return (
    <div data-testid="pt-certificates-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure testing"
        title="Certificates"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          {
            icon: <FileClock className="size-4" aria-hidden="true" />,
            label: 'To issue',
            sub: 'Drafts',
            tone: 'accent',
            loading: countsLoading,
            value: displayValue(tally((c) => c.status === 'draft')),
          },
          {
            icon: <Undo2 className="size-4" aria-hidden="true" />,
            label: 'Rejected',
            sub: 'Need a rebuild',
            tone: 'accent',
            loading: countsLoading,
            value: displayValue(tally((c) => c.status === 'rejected')),
          },
          {
            icon: <CheckCircle2 className="size-4" aria-hidden="true" />,
            label: 'Issued',
            sub: 'All time',
            tone: 'success',
            loading: countsLoading,
            value: displayValue(tally((c) => c.status === 'issued')),
          },
          {
            icon: <AlertOctagon className="size-4" aria-hidden="true" />,
            label: 'Failures',
            sub: 'Failed a test',
            tone: 'accent',
            loading: countsLoading,
            value: displayValue(tally((c) => c.kind === 'failure')),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
            {CHIPS.map((c) => (
              <PtChip key={c.key || 'all'} label={c.label} active={status === c.key} onClick={() => setStatus(c.key)} count={chipCount(c.key)} />
            ))}
          </div>

          <label className="relative block">
            <span className="sr-only">Search certificate number, job, customer, location, UWI or LSD</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => onSearchInput(e.target.value)}
              placeholder="Search certificate number, job, customer, location, UWI or LSD"
              className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </label>

          <PtPanel
            eyebrow="Test certificates"
            title="Certificates"
            testId="pt-certificate-register"
            footer={!listQuery.isLoading && !listFailure && rows.length > 0 ? `${rows.length} certificate${rows.length === 1 ? '' : 's'}` : undefined}
          >
            {listQuery.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading certificates" />
              </div>
            ) : listFailure ? (
              <LoadFailureNotice error={queryFailureError(listQuery)} kind={listFailure} subject="certificates" />
            ) : rows.length === 0 ? (
              <PtEmpty>
                {q
                  ? 'No certificate matches this search.'
                  : status === 'draft'
                    ? 'No certificates are awaiting issue.'
                    : status === 'issued'
                      ? 'No certificates have been issued.'
                      : status === 'rejected'
                        ? 'No certificates are rejected.'
                        : 'There are no certificates. A certificate is drafted when a test job is completed.'}
              </PtEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1080px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Cert #</th>
                        <th scope="col" className="px-4 py-3">Job</th>
                        <th scope="col" className="px-4 py-3">Customer / location</th>
                        <th scope="col" className="px-4 py-3">Tests passed</th>
                        <th scope="col" className="px-4 py-3">Kind</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3 text-right">Flags</th>
                        <th scope="col" className="px-4 py-3">Date</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((c) => {
                        const d = rowDate(c, tz);
                        return (
                          <tr
                            key={c.id}
                            role="button"
                            tabIndex={0}
                            onClick={() => open(c.id)}
                            onKeyDown={rowKeys(c.id)}
                            data-testid="pt-cert-row"
                            className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${ptCertStatusSkin(c.status).rail}`}
                          >
                            <td className="px-4 py-3 font-mono text-sm font-extrabold text-white">{c.certificate_number || 'Draft'}</td>
                            <td className="px-4 py-3 font-mono text-sm text-fieldiq-text">{c.job_number || '—'}</td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-semibold text-white">{c.customer_name || '—'}</p>
                              <p className="mt-0.5 text-xs text-fieldiq-muted">{[c.location_name, c.lsd].filter(Boolean).join(' · ') || '—'}</p>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 font-mono text-sm tabular-nums text-fieldiq-text">{testsFor(c)}</td>
                            <td className="px-4 py-3"><PtSkinBadge skin={ptCertKindSkin(c.kind)} /></td>
                            <td className="px-4 py-3"><PtSkinBadge skin={ptCertStatusSkin(c.status)} testId="pt-cert-row-status" value={c.status} /></td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text" data-testid="pt-cert-row-flags">{flagText(c.flag_count)}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">
                              <p>{d.text}</p>
                              <p className="mt-0.5 text-xs text-fieldiq-muted">{d.sub}</p>
                            </td>
                            <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((c) => {
                    const d = rowDate(c, tz);
                    return (
                      <div
                        key={c.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => open(c.id)}
                        onKeyDown={rowKeys(c.id)}
                        className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-mono text-sm font-extrabold text-white">{c.certificate_number || 'Draft'}</p>
                            <p className="mt-0.5 truncate text-xs text-fieldiq-muted">
                              {c.job_number || '—'} · {c.customer_name || '—'}
                            </p>
                          </div>
                          <PtSkinBadge skin={ptCertStatusSkin(c.status)} />
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-fieldiq-text">
                          <PtSkinBadge skin={ptCertKindSkin(c.kind)} />
                          <span>{d.sub} {d.text}</span>
                          <span>Tests {testsFor(c)}</span>
                          <span>Flags {flagText(c.flag_count)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </PtPanel>
        </div>
      </section>
    </div>
  );
}
